import { includesIgnoringAccents } from "../utils/sbcListControls";
import { completedIndexOf, galleryVerdict, tokensUpTo } from "./grades";
export const GALLERY_SORTS = [
    "newest",
    "recommended",
    "tokens",
    "cheapest-tokens",
    "transfers",
    "name",
];
export const GALLERY_SORT_LABEL_KEYS = {
    newest: "sort.newest",
    recommended: "sort.recommended",
    tokens: "sort.galleryTokens",
    "cheapest-tokens": "sort.cheapestTokens",
    transfers: "sort.transfers",
    name: "sort.name",
};
export const GALLERY_STATUS_LABEL_KEYS = {
    all: "status.all",
    available: "status.available",
    completed: "status.completed",
};
export const isGallerySort = (value) => GALLERY_SORTS.includes(value);
/**
 * The grade a set shows under the active sort and the set's sort key (lower
 * first). Only reachable, on-market grades above the completed one count (and a
 * planned grade `at`, which may need cards the market doesn't sell); none
 * leaves the set without a pick, sorted last. `at` fixes the grade (a planned one)
 * and only works out its key.
 */
export const pickGalleryGrade = (set, grades, completedGrade, sort, at, 
/** Coins from the latest Club Import: Recommended puts grades above them last. */
coins) => {
    // Newest: latest EA release first, showing the Recommended grade.
    if (sort === "newest") {
        const pick = pickGalleryGrade(set, grades, completedGrade, "recommended", at, coins);
        return pick && { index: pick.index, key: [-(set.startTime ?? 0)] };
    }
    const completedIndex = completedIndexOf(set.grades, completedGrade);
    // Tax loss per token the grade adds over the completed ones.
    const costPerToken = (i) => {
        const added = tokensUpTo(set.grades, i) - tokensUpTo(set.grades, completedIndex);
        return added > 0 ? (grades[i]?.taxLoss ?? 0) / added : Infinity;
    };
    const transfers = (i) => (grades[i]?.size ?? 0) - (grades[i]?.collectedCount ?? 0);
    const open = grades.flatMap((g, i) => i > completedIndex && g.reachable && (g.onMarket || i === at) ? [i] : []);
    const highest = open[open.length - 1];
    if (highest === undefined)
        return undefined;
    // A-Z orders by name alone; the card shows its highest open grade.
    if (sort === "name")
        return { index: at ?? highest, key: [] };
    if (sort === "tokens") {
        const index = at ?? highest;
        // The tokens still to earn: the highest open grade over the completed ones.
        const reachable = tokensUpTo(set.grades, index) - tokensUpTo(set.grades, completedIndex);
        return { index, key: [-reachable, costPerToken(index)] };
    }
    // Ties go to the higher grade.
    if (sort === "cheapest-tokens") {
        const index = at ??
            open.reduce((best, i) => costPerToken(i) <= costPerToken(best) ? i : best);
        // Equal cost per token: more tokens first.
        return {
            index,
            key: [
                costPerToken(index),
                tokensUpTo(set.grades, completedIndex) - tokensUpTo(set.grades, index),
            ],
        };
    }
    // Recommended: affordable grades first, then thumbs-up ones, by fewest players to buy; a set without one shows its fewest-buys grade after them.
    const affordable = (i) => coins === undefined || (grades[i]?.budgetNeeded ?? 0) <= coins;
    const pool = sort === "recommended" && open.some(affordable)
        ? open.filter(affordable)
        : open;
    const good = sort === "recommended"
        ? pool.filter((i) => galleryVerdict(costPerToken(i)) === "good")
        : [];
    const index = at ??
        (good.length ? good : pool).reduce((best, i) => transfers(i) <= transfers(best) ? i : best);
    // Ties: more tokens first, then A-Z (in the comparators).
    return {
        index,
        key: [
            ...(sort === "recommended"
                ? [affordable(index) ? 0 : 1, good.includes(index) ? 0 : 1]
                : []),
            transfers(index),
            tokensUpTo(set.grades, completedIndex) - tokensUpTo(set.grades, index),
        ],
    };
};
/** Per set: the user's stored solution, else the market-only solve for their console. */
export const galleryGradesBySetId = (categories, progress, platform) => new Map(categories
    .flatMap((c) => c.sets)
    .map((s) => [
    s.id,
    progress.get(s.id)?.solution?.grades ??
        (platform === "PC" ? s.market.pc : s.market.ps),
]));
/**
 * A snapshot of the order, each set's grade and the S-completed sets. Hosts
 * keep it until the next re-sort: a load, a new sort, category or status, or a
 * poll of a solve run.
 */
export const galleryOrder = ({ categories, gradesBySetId, progress, sort, plannedGrades, coins, }) => {
    const sets = categories.flatMap((c) => c.sets);
    const picks = new Map(sets.map((s) => {
        const planned = plannedGrades?.get(s.id);
        return [
            s.id,
            pickGalleryGrade(s, gradesBySetId.get(s.id) ?? [], progress.get(s.id)?.completedGrade ?? null, sort, 
            // A plan's sets sort on their planned grade.
            planned ? s.grades.findIndex((g) => g.name === planned) : undefined, coins),
        ];
    }));
    const names = new Map(sets.map((s) => [s.id, s.name]));
    const orderedIds = [...picks.keys()].sort((a, b) => {
        const byName = (names.get(a) ?? "").localeCompare(names.get(b) ?? "");
        if (sort === "name")
            return byName;
        const ka = picks.get(a)?.key;
        const kb = picks.get(b)?.key;
        // Sets without a pick go last; every tie goes A-Z.
        if (!ka || !kb)
            return (ka ? 0 : 1) - (kb ? 0 : 1) || byName;
        const i = ka.findIndex((v, j) => v !== kb[j]);
        return i === -1 ? byName : (ka[i] ?? 0) - (kb[i] ?? 0);
    });
    return {
        orderedIds,
        sortPicks: new Map([...picks].map(([id, pick]) => [id, pick?.index])),
        completedIds: new Set([...progress.values()]
            .filter((p) => p.completedGrade === "S")
            .map((p) => p.setId)),
    };
};
/**
 * The sets a category shows under the search, plan and status, in the frozen
 * order. A plan puts its sets first and the rest in `notInPlan`; a search
 * ignores the plan and shows every match.
 */
export const visibleGallerySets = ({ sets, searchTerm, plannedGrades, status, completedIds, orderedIds, }) => {
    const searched = sets.filter((s) => !searchTerm || includesIgnoringAccents(s.name, searchTerm));
    const rank = new Map(orderedIds.map((id, i) => [id, i]));
    const byStatus = (s) => status === "all" ||
        (status === "completed" ? completedIds.has(s.id) : !completedIds.has(s.id));
    const ordered = searched.sort((a, b) => (rank.get(a.id) ?? rank.size) - (rank.get(b.id) ?? rank.size));
    const plan = searchTerm ? undefined : plannedGrades;
    // A plan shows all its sets, the ones completed since it was built too.
    const visible = plan
        ? ordered.filter((s) => plan.has(s.id))
        : ordered.filter(byStatus);
    const notInPlan = plan
        ? ordered.filter((s) => !plan.has(s.id) && byStatus(s))
        : [];
    const completedCount = searched.filter((s) => completedIds.has(s.id)).length;
    return {
        visible,
        notInPlan,
        statusCounts: {
            all: searched.length,
            available: searched.length - completedCount,
            completed: completedCount,
        },
    };
};
/** Tokens earned so far: each set's completed grade pays its own and every grade's below it. */
export const earnedGalleryTokens = (sets, progress) => sets.reduce((sum, s) => sum +
    tokensUpTo(s.grades, completedIndexOf(s.grades, progress.get(s.id)?.completedGrade)), 0);
/**
 * The planner's Tokens placeholder: every set's highest reachable grade over
 * the completed one, cards the market doesn't sell included.
 */
export const maxOpenGalleryTokens = (sets, gradesBySetId, progress) => sets.reduce((sum, s) => {
    const completedIndex = completedIndexOf(s.grades, progress.get(s.id)?.completedGrade);
    const top = (gradesBySetId.get(s.id) ?? []).findLastIndex((g, i) => i > completedIndex && g.reachable);
    return top === -1
        ? sum
        : sum + tokensUpTo(s.grades, top) - tokensUpTo(s.grades, completedIndex);
}, 0);
/**
 * Every set a build would solve (S-completed ones skipped) has a solution from
 * after the latest content release and the newest collected player.
 */
export const gallerySolutionsUpToDate = (sets, progress) => {
    const toSolve = sets.filter((s) => progress.get(s.id)?.completedGrade !== "S");
    return {
        toSolveCount: toSolve.length,
        upToDate: toSolve.length > 0 &&
            toSolve.every((s) => progress.get(s.id)?.solution?.upToDate),
    };
};
//# sourceMappingURL=list.js.map